import { useState } from 'react';
import type { DiagnosticMetrics } from '../types/wasm';
import { Tooltip } from './Tooltip';

interface DiagnosticsPanelProps {
  diagnostics: DiagnosticMetrics;
  modelType: string;
}

export function DiagnosticsPanel({ diagnostics, modelType }: DiagnosticsPanelProps) {
  const [metricsTab, setMetricsTab] = useState<'results' | 'explanation'>('results');
  const [criteriaTab, setCriteriaTab] = useState<'results' | 'explanation'>('results');

  // Helper function to get quality rating based on MAPE
  const getQualityRating = (mape: number): { label: string; color: string } => {
    if (mape < 10) return { label: 'Excellent', color: 'text-green-600 bg-green-50' };
    if (mape < 20) return { label: 'Good', color: 'text-blue-600 bg-blue-50' };
    if (mape < 30) return { label: 'Fair', color: 'text-yellow-600 bg-yellow-50' };
    return { label: 'Poor', color: 'text-red-600 bg-red-50' };
  };

  const quality = getQualityRating(diagnostics.mape);

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Model Diagnostics</h2>
        <button
          onClick={() => {
            alert("Want automated AI explanations for Model Diagnostics? Request this feature in the feedback form at the bottom of the page!");
            document.getElementById('feedback-form')?.scrollIntoView({ behavior: 'smooth' });
          }}
          className="text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center space-x-1 cursor-pointer"
        >
          <span>💡</span>
          <span>Request AI features</span>
        </button>
      </div>

      {/* Overall Quality Badge */}
      <div className="mb-6 p-4 rounded-lg border-2 border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-600 mb-1">Forecast Quality</div>
            <div className={`inline-block px-4 py-2 rounded-full font-semibold text-lg ${quality.color}`}>
              {quality.label}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600">Model Type</div>
            <div className="text-lg font-semibold text-gray-900">
              {modelType === 'arima' ? 'ARIMA' : 'Exponential Smoothing'}
            </div>
          </div>
        </div>
      </div>

      {/* Error Metrics */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Error Metrics</h3>
          <div className="flex bg-gray-100 p-0.5 rounded-lg border border-gray-200">
            <button
              type="button"
              onClick={() => setMetricsTab('results')}
              className={`px-3 py-1 text-[11px] font-semibold rounded-md transition-all cursor-pointer ${
                metricsTab === 'results'
                  ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              Metrics View
            </button>
            <button
              type="button"
              onClick={() => setMetricsTab('explanation')}
              className={`px-3 py-1 text-[11px] font-semibold rounded-md transition-all cursor-pointer ${
                metricsTab === 'explanation'
                  ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              What is this?
            </button>
          </div>
        </div>

        {metricsTab === 'results' ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* MAPE */}
            <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
              <div className="flex items-center space-x-1 mb-1">
                <div className="text-xs font-medium text-blue-600 uppercase tracking-wide">
                  MAPE
                </div>
                <Tooltip content="Mean Absolute Percentage Error - Shows average forecast error as a percentage. Lower is better. <10%: Excellent, 10-20%: Good, 20-30%: Fair, >30%: Poor">
                  <svg className="w-3 h-3 text-blue-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </Tooltip>
              </div>
              <div className="text-3xl font-bold text-blue-900 mb-1">
                {diagnostics.mape.toFixed(2)}%
              </div>
              <div className="text-xs text-blue-700">
                Mean Absolute Percentage Error
              </div>
              <div className="mt-2 text-xs text-blue-600">
                {diagnostics.mape < 10 ? '📈 High accuracy' : diagnostics.mape < 20 ? '✓ Acceptable' : '⚠️ Consider tuning'}
              </div>
            </div>

            {/* RMSE */}
            <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
              <div className="flex items-center space-x-1 mb-1">
                <div className="text-xs font-medium text-purple-600 uppercase tracking-wide">
                  RMSE
                </div>
                <Tooltip content="Root Mean Squared Error - Penalizes large errors more heavily than small ones. Measured in same units as your data. Lower values indicate better model fit.">
                  <svg className="w-3 h-3 text-purple-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </Tooltip>
              </div>
              <div className="text-3xl font-bold text-purple-900 mb-1">
                {diagnostics.rmse.toFixed(4)}
              </div>
              <div className="text-xs text-purple-700">
                Root Mean Squared Error
              </div>
              <div className="mt-2 text-xs text-purple-600">
                Lower is better
              </div>
            </div>

            {/* MAE */}
            <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
              <div className="flex items-center space-x-1 mb-1">
                <div className="text-xs font-medium text-green-600 uppercase tracking-wide">
                  MAE
                </div>
                <Tooltip content="Mean Absolute Error - Average of absolute differences between predicted and actual values. Easier to interpret than RMSE. Measured in same units as your data.">
                  <svg className="w-3 h-3 text-green-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </Tooltip>
              </div>
              <div className="text-3xl font-bold text-green-900 mb-1">
                {diagnostics.mae.toFixed(4)}
              </div>
              <div className="text-xs text-green-700">
                Mean Absolute Error
              </div>
              <div className="mt-2 text-xs text-green-600">
                Average prediction error
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-slate-50 border border-gray-200/60 rounded-xl p-4 text-xs text-gray-600 leading-relaxed space-y-2">
            <p className="font-bold text-gray-800">Understanding Error Metrics</p>
            <p>
              Error metrics quantify how closely the model's fitted values match the actual historical data. They help gauge prediction accuracy and evaluate potential forecast risk.
            </p>
            <ul className="list-disc list-inside space-y-1 pl-1 text-[11px] text-gray-500">
              <li><strong>MAPE (Mean Absolute Percentage Error):</strong> Expresses forecast error as a percentage. Easier to interpret across different scales. Below 10% indicates excellent accuracy, 10-20% is good, 20-30% is fair, and above 30% is poor.</li>
              <li><strong>RMSE (Root Mean Squared Error):</strong> Measures error magnitude in the same units as the data. Penalizes larger discrepancies more heavily because errors are squared before averaging.</li>
              <li><strong>MAE (Mean Absolute Error):</strong> Represents the average absolute error magnitude. Unlike RMSE, it treats all errors linearly.</li>
            </ul>
          </div>
        )}

        {/* Information Criteria (for ARIMA) */}
        {(diagnostics.aic !== undefined || diagnostics.bic !== undefined) && (
          <div className="mt-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Information Criteria</h3>
              <div className="flex bg-gray-100 p-0.5 rounded-lg border border-gray-200">
                <button
                  type="button"
                  onClick={() => setCriteriaTab('results')}
                  className={`px-3 py-1 text-[11px] font-semibold rounded-md transition-all cursor-pointer ${
                    criteriaTab === 'results'
                      ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  Results View
                </button>
                <button
                  type="button"
                  onClick={() => setCriteriaTab('explanation')}
                  className={`px-3 py-1 text-[11px] font-semibold rounded-md transition-all cursor-pointer ${
                    criteriaTab === 'explanation'
                      ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  What is this?
                </button>
              </div>
            </div>

            {criteriaTab === 'results' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {diagnostics.aic !== undefined && diagnostics.aic !== null && (
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-1 mb-1">
                      <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                        AIC (Akaike Information Criterion)
                      </div>
                      <Tooltip content="Akaike Information Criterion - Measures model quality while penalizing complexity. Use to compare models: lower AIC = better model. Only comparable between models on same data.">
                        <svg className="w-3 h-3 text-gray-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </Tooltip>
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {diagnostics.aic.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      Use to compare models - lower is better
                    </div>
                  </div>
                )}

                {diagnostics.bic !== undefined && diagnostics.bic !== null && (
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-1 mb-1">
                      <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                        BIC (Bayesian Information Criterion)
                      </div>
                      <Tooltip content="Bayesian Information Criterion - Similar to AIC but penalizes model complexity more strongly. Prefers simpler models. Lower BIC = better model. Use for model comparison.">
                        <svg className="w-3 h-3 text-gray-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </Tooltip>
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {diagnostics.bic.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      Penalizes model complexity more than AIC
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-slate-50 border border-gray-200/60 rounded-xl p-4 text-xs text-gray-600 leading-relaxed space-y-2">
                <p className="font-bold text-gray-800">Understanding AIC & BIC</p>
                <p>
                  Information criteria evaluate model quality by balancing goodness of fit with model simplicity. They are used for comparing different candidate models on the exact same dataset.
                </p>
                <ul className="list-disc list-inside space-y-1 pl-1 text-[11px] text-gray-500">
                  <li><strong>AIC (Akaike Information Criterion):</strong> Estimates relative information lost by a model. A lower value indicates a better model. It penalizes complex models containing many parameters.</li>
                  <li><strong>BIC (Bayesian Information Criterion):</strong> Similar to AIC but applies a stricter penalty for parameters based on sample size. It favors simpler models to protect against overfitting.</li>
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Interpretation Guide */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">📊 Interpretation Guide</h4>
          <ul className="text-xs text-blue-800 space-y-1">
            <li><strong>MAPE &lt; 10%:</strong> Excellent forecast accuracy</li>
            <li><strong>MAPE 10-20%:</strong> Good forecast, acceptable for most applications</li>
            <li><strong>MAPE 20-30%:</strong> Fair, consider parameter tuning or different model</li>
            <li><strong>MAPE &gt; 30%:</strong> Poor accuracy, model may not be suitable for this data</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
